import { i18n } from 'i18n';
import { BasicDisplayUnits, UnitBase, unitLabels } from 'hooks/useDisplayUnits/useDisplayUnits';
import { FlushStageType, FlushVolumeMode } from 'providers/api';

// Every mode, in display order. Enumerated by hand (not reflected off the enum) so the order is
// stable and a new member has to be placed deliberately.
export const FLUSH_VOLUME_MODES: FlushVolumeMode[] = [
  FlushVolumeMode.PipeVolumeMultiple,
  FlushVolumeMode.PipeVolumePlus,
  FlushVolumeMode.Explicit,
  FlushVolumeMode.Distance,
  FlushVolumeMode.AirStartVolume,
];

const assertNever = (kind: string, value: never): never => {
  throw new Error(`Unhandled ${kind} ${String(value)}`);
};

export const flushStageTypeLabel = (stageType: FlushStageType): string => {
  switch (stageType) {
    case FlushStageType.PastePump:
      return i18n._('Paste pump');
    case FlushStageType.FlushPump:
      return i18n._('Flush pump');
    default:
      return assertNever('flush stage type', stageType);
  }
};

export const flushVolumeModeLabel = (mode: FlushVolumeMode): string => {
  switch (mode) {
    case FlushVolumeMode.Explicit:
      return i18n._('Fixed volume');
    case FlushVolumeMode.PipeVolumeMultiple:
      return i18n._('Multiple of pipe volume');
    case FlushVolumeMode.PipeVolumePlus:
      return i18n._('Pipe volume plus a fixed volume');
    case FlushVolumeMode.Distance:
      return i18n._('Distance down the line');
    case FlushVolumeMode.AirStartVolume:
      return i18n._('Until the air purge can start');
    default:
      return assertNever('flush volume mode', mode);
  }
};

// The label for the number that accompanies the mode, without a unit: the field showing it decides
// whether the value is displayed converted or in metric. AirStartVolume takes no number.
export const flushVolumeValueLabel = (mode: FlushVolumeMode): string => {
  switch (mode) {
    case FlushVolumeMode.Explicit:
      return i18n._('Flush volume');
    case FlushVolumeMode.PipeVolumeMultiple:
      return i18n._('Multiple of pipe volume');
    case FlushVolumeMode.PipeVolumePlus:
      return i18n._('Additional volume');
    case FlushVolumeMode.Distance:
      return i18n._('Distance down the line');
    case FlushVolumeMode.AirStartVolume:
      return i18n._('Not used for this mode');
    default:
      return assertNever('flush volume mode', mode);
  }
};

// The unit of the number that accompanies the mode, for callers that show the metric value unconverted.
// Undefined for the modes whose number is a plain multiplier or unused.
export const flushVolumeValueUnit = (mode: FlushVolumeMode): UnitBase | undefined => {
  switch (mode) {
    case FlushVolumeMode.Explicit:
    case FlushVolumeMode.PipeVolumePlus:
      return unitLabels[BasicDisplayUnits.Volume];
    case FlushVolumeMode.Distance:
      return unitLabels[BasicDisplayUnits.Distance];
    case FlushVolumeMode.PipeVolumeMultiple:
    case FlushVolumeMode.AirStartVolume:
      return undefined;
    default:
      return assertNever('flush volume mode', mode);
  }
};
